import { useState } from 'react';
import type { CSSProperties } from 'react';
import { loginUser } from '../../../api/user';
import type { AnonymousUser } from '../../../api/types';

interface WelcomeScreenProps {
  onNext: () => void;
  onLoginSuccess?: (user: AnonymousUser) => void;
}

export default function WelcomeScreen({ onNext, onLoginSuccess }: WelcomeScreenProps) {
  const [loginOpen, setLoginOpen] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const canLogin = login.trim().length >= 3 && password.length >= 8 && !loginLoading;

  const handleLogin = async () => {
    if (!canLogin) return;
    setLoginLoading(true);
    setLoginError(null);
    try {
      const user = await loginUser({ login: login.trim(), password });
      onLoginSuccess?.(user);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Не удалось войти');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div
      className="relative flex flex-col w-full"
      style={{
        background: 'linear-gradient(170deg, #1A1B2E 0%, #2D1B3D 60%, #1F1A35 100%)',
        minHeight: '100dvh',
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 'calc(14px + env(safe-area-inset-top))',
          right: '16px',
          zIndex: 30,
        }}
      >
        <button
          onClick={() => {
            setLoginOpen((open) => !open);
            setLoginError(null);
          }}
          style={{
            height: '36px',
            padding: '0 14px',
            borderRadius: '18px',
            border: '1px solid rgba(255,255,255,0.16)',
            background: loginOpen ? 'rgba(62,207,160,0.18)' : 'rgba(15,16,32,0.52)',
            color: '#F5F5F7',
            fontSize: '14px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            cursor: 'pointer',
            backdropFilter: 'blur(16px)',
          }}
        >
          <i className="ri-login-circle-line" style={{ fontSize: '17px', color: '#3ECFA0' }} />
          Войти
        </button>

        {loginOpen && (
          <div
            style={{
              position: 'absolute',
              top: '44px',
              right: 0,
              width: 'min(320px, calc(100vw - 32px))',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(20,21,38,0.96)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
              padding: '14px',
              backdropFilter: 'blur(22px)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                value={login}
                onChange={(event) => setLogin(event.target.value)}
                placeholder="Email или телефон"
                autoComplete="username"
                style={loginInputStyle}
              />
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Пароль"
                autoComplete="current-password"
                style={loginInputStyle}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void handleLogin();
                }}
              />
              {loginError && (
                <div
                  style={{
                    borderRadius: '12px',
                    background: 'rgba(255,107,107,0.12)',
                    border: '1px solid rgba(255,107,107,0.26)',
                    color: '#FFD1D1',
                    fontSize: '12px',
                    lineHeight: 1.45,
                    padding: '9px 10px',
                  }}
                >
                  {loginError}
                </div>
              )}
              <button
                disabled={!canLogin}
                onClick={handleLogin}
                style={{
                  height: '44px',
                  borderRadius: '14px',
                  border: 'none',
                  background: canLogin ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)' : 'rgba(255,255,255,0.08)',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: 700,
                  opacity: canLogin ? 1 : 0.56,
                  cursor: canLogin ? 'pointer' : 'default',
                }}
              >
                {loginLoading ? 'Входим...' : 'Войти в профиль'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Background ambient blobs */}
      <div
        style={{
          position: 'absolute', top: '-80px', right: '-60px', width: '280px', height: '280px',
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(62,207,160,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute', bottom: '120px', left: '-80px', width: '240px', height: '240px',
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(74,144,226,0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Logo area */}
      <div className="flex flex-col items-center pt-12 pb-1">
        <div
          className="rounded-3xl overflow-hidden flex items-center justify-center"
          style={{
            width: 'clamp(64px, 18vw, 80px)',
            height: 'clamp(64px, 18vw, 80px)',
            boxShadow: '0 8px 32px rgba(62,207,160,0.3)',
          }}
        >
          <img
            src="/images/onboarding/omut-logo.png"
            alt="Омут Памяти"
            className="w-full h-full object-cover"
          />
        </div>
        <p style={{ fontSize: '13px', color: '#6B7A94', marginTop: '10px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Омут Памяти</p>
      </div>

      {/* Center illustration */}
      <div
        className="flex-1 flex items-center justify-center px-6"
        style={{ minHeight: 0, paddingTop: '8px', paddingBottom: '8px' }}
      >
        <div
          style={{
            position: 'relative',
            width: 'min(54vw, 220px)',
            height: 'min(54vw, 220px)',
            minWidth: '170px',
            minHeight: '170px',
          }}
        >
          <img
            src="/images/onboarding/welcome-water.jpg"
            alt=""
            className="w-full h-full rounded-full object-cover"
            style={{ opacity: 0.85, boxShadow: '0 0 60px rgba(62,207,160,0.25), 0 0 120px rgba(74,144,226,0.15)' }}
          />
          {/* Ripple rings */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: `${100 + i * 18}%`,
                height: `${100 + i * 18}%`,
                borderRadius: '50%',
                border: `1px solid rgba(62,207,160,${0.15 - i * 0.04})`,
                pointerEvents: 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* Text block */}
      <div
        className="px-7"
        style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
      >
        <h1 style={{ fontSize: 'clamp(23px, 6vw, 26px)', fontWeight: 700, color: '#F5F5F7', lineHeight: 1.3, marginBottom: '12px' }}>
          Добро пожаловать в Омут Памяти.
        </h1>
        <p style={{ fontSize: 'clamp(15px, 4.2vw, 17px)', fontWeight: 400, color: '#A8B2C8', lineHeight: 1.55, marginBottom: '6px' }}>
          Здесь ваши воспоминания больше не будут исчезать.
        </p>
        <p style={{ fontSize: '17px', fontWeight: 600, color: '#3ECFA0', marginBottom: '24px' }}>
          Начнём?
        </p>

        <button
          onClick={onNext}
          className="w-full active:scale-95 transition-transform duration-150"
          style={{
            height: '56px',
            borderRadius: '28px',
            background: 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
            border: 'none',
            fontSize: '17px',
            fontWeight: 600,
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(62,207,160,0.35)',
          }}
        >
          Продолжить
        </button>
      </div>
    </div>
  );
}

const loginInputStyle: CSSProperties = {
  width: '100%',
  height: '44px',
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.07)',
  color: '#F5F5F7',
  fontSize: '15px',
  padding: '0 12px',
  outline: 'none',
  boxSizing: 'border-box',
};
